<template>
  <div>
    <b-row no-gutters>
      <b-col sm="6">
        <h1 class="mt-0">{{moduleData.longname}}</h1>
      </b-col>
      <b-col sm="2">
        <b-button
          class="mt-1"
          size="sm"
          variant="outline-secondary"
          @click="collapseAll()"
          block
        >{{ $t('project.projectViewer.collapseAll') }}</b-button>
      </b-col>
      <b-col sm="2">
        <b-button
          class="mt-1"
          size="sm"
          variant="outline-secondary"
          @click="expandAll()"
          block
        >{{ $t('project.projectViewer.expandAll') }}</b-button>
      </b-col>
      <b-col sm="2">
        <b-button
          v-if="adminRights"
          class="mt-1 mb-3"
          size="sm"
          variant="outline-secondary"
          @click="exportExpanded()"
          block
        >{{ $t('project.projectViewer.exportExpanded') }}</b-button>
      </b-col>
    </b-row>
    <div v-for="(value, key) in moduleData.objects" :key="key" class="m-1">
      <b-button
        block
        v-b-toggle="objectId(key)"
        size="lg"
        variant="info"
        class="text-left"
      >{{value.objectname}}</b-button>
      <b-collapse :id="objectId(key)" v-model="value.collapse">
        <div v-for="(valueProp, keyProp) in value.properties" :key="keyProp" class="m-1">
          <b-button
            block
            v-b-toggle="propId(key,keyProp)"
            variant="light"
            class="text-left"
          >{{valueProp.propertyname}}</b-button>
          <b-collapse :id="propId(key,keyProp)" v-model="valueProp.collapse">
            <!-- Main table variable element -->
            <b-table
              show-empty
              stacked="md"
              :items="valueProp.variables"
              :fields="fields"
              small
              hover
              thead-class="hidden_header"
              class="m-1"
              bordered
            >
              <template #cell(calcValue)="row">{{calculateValue(row.item)}}</template>

              <template #cell(varDesc)="row">
                <b-input-group>
                  <b-input-group-prepend v-if="row.item.type >= 16">
                    <b-form-input
                      type="number"
                      min="0"
                      :max="row.item.length - 1"
                      size="sm"
                      v-model="row.item.arrayIndex"
                      style="width: 50px; margin-right: 5px;"
                    ></b-form-input>
                  </b-input-group-prepend>
                  {{variableDesc(row.item)}}
                </b-input-group>
              </template>

              <template #cell(actions)="row">
                <b-input-group v-if="row.item.changeable && adminRights">
                  <b-form-input
                    v-if="getEditInput(row.item) === null"
                    v-model.number="row.item.newValue"
                    type="number"
                    step="any"
                    size="sm"
                  ></b-form-input>
                  <b-form-select
                    v-else
                    v-model="row.item.newValue"
                    :options="getEditInput(row.item)"
                    size="sm"
                  ></b-form-select>
                  <b-input-group-append>
                    <b-button
                      size="sm"
                      v-b-tooltip.hover
                      :title="$t('project.projectViewer.sendValueTooltip')"
                      :variant="getSetButtonVariant(row.item)"
                      @click="sendNewValue(row.item)"
                    >
                      <i class="icon-arrow-right-circle" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </template>
            </b-table>
          </b-collapse>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<style>
.hidden_header {
  display: none;
}

@media (min-width: 772px) {
  .value_column {
    width: 10%;
  }

  .edit_column {
    width: 20%;
  }
}
</style>

<script>
import {
  decodeVariable,
  codeVariable,
  getEditFormInput,
  csvExport
} from "@/_helpers";

export default {
  name: "ModuleViewer",
  props: {
    adminRights: {
      type: Boolean,
      default: false
    },
    moduleData: {
      type: Object,
      default() {return {};}
    }
  },
  data() {
    return {
      variableToSet: null,
      loading: true,
      fields: [
        {
          key: "calcValue",
          label: this.$i18n.t("project.projectViewer.viewerValue"),
          tdClass: "value_column"
        },
        {
          key: "varDesc",
          label: this.$i18n.t("project.projectViewer.viewerDesc")
        },
        {
          key: "actions",
          label: this.$i18n.t("project.projectViewer.viewerEdit"),
          tdClass: "edit_column"
        }
      ]
    };
  },
  created() {
    this.loading = false;
  },
  methods: {
    collapseAll() {
      for (let i = 0; i < this.moduleData.objects.length; i++) {
        this.moduleData.objects[i].collapse = false;
        for (let j = 0; j < this.moduleData.objects[i].properties.length; j++) {
          this.moduleData.objects[i].properties[j].collapse = false;
        }
      }
    },
    expandAll() {
      for (let i = 0; i < this.moduleData.objects.length; i++) {
        this.moduleData.objects[i].collapse = true;
        for (let j = 0; j < this.moduleData.objects[i].properties.length; j++) {
          this.moduleData.objects[i].properties[j].collapse = true;
        }
      }
    },
    exportExpanded() {
      var csvContent = "";
      for (let i = 0; i < this.moduleData.objects.length; i++) {
        if (!this.moduleData.objects[i].collapse) continue;
        csvContent += "\n" + this.moduleData.objects[i].objectname + "\n";
        var first = true;
        for (let j = 0; j < this.moduleData.objects[i].properties.length; j++) {
          if (!this.moduleData.objects[i].properties[j].collapse) continue;
          if (!first) csvContent += "\n";
          csvContent +=
            "---> " +
            this.moduleData.objects[i].properties[j].propertyname +
            "\n";
          first = false;
          for (
            let l = 0;
            l < this.moduleData.objects[i].properties[j].variables.length;
            l++
          ) {
            const item = this.moduleData.objects[i].properties[j].variables[l];
            csvContent +=
              this.calculateValue(item) + ";" + this.variableDesc(item) + "\n";
          }
        }
      }
      if (!csvContent) return;
      var now = new Date();
      const dateString =
        now.getFullYear() +
        "-" +
        (now.getMonth() + 1) +
        "-" +
        now.getDate() +
        "_" +
        now.getHours() +
        "-" +
        now.getMinutes();
      const fileName = this.moduleData.longname + "_" + dateString + ".csv";
      var header = this.moduleData.longname + ";" + dateString + "\n";
      csvExport(header.concat(csvContent), fileName);
    },
    objectId(key) {
      return this.moduleData.shortname + "-object-" + key;
    },
    propId(key, keyProp) {
      return this.moduleData.shortname + "-prop-" + key + "-" + keyProp;
    },
    variableDesc(variable) {
      if (variable.userDescription) return variable.userDescription;
      return variable.defaultDescription;
    },
    calculateValue(variable) {
      return decodeVariable(variable);
    },
    getEditInput(variable) {
      // options for changing the value
      return getEditFormInput(variable);
    },
    getSetButtonVariant(item) {
      if (item.setValueResult === 0) return "outline-dark";
      if (item.setValueResult === 1) return "outline-success";
      return "outline-danger";
    },
    setValueCallback(res) {
      if (!this.variableToSet) return;
      this.variableToSet.setValueResult = res;
      this.variableToSet = null;
    },
    sendNewValue(variable) {
      if (variable.newValue === variable.value) return;
      variable.setValueResult = 0;
      var codeNewValue = codeVariable(variable);
      var toSend;
      if (codeNewValue === null) {
        toSend = {
          success: false,
          value: this.$i18n.t("project.projectViewer.invalidInput"),
          vari: null,
          repre: null,
          type: null
        };
      } else {
        toSend = {
          success: true,
          value: codeNewValue,
          vari: variable.variable,
          repre: variable.representation,
          type: variable.type
        };
        variable.newValue = null;
      }
      this.variableToSet = variable;
      this.$emit("sendValue", toSend, this.setValueCallback);
    }
  }
};
</script>
